import { Box, CircularProgress, CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { theme, CALIBRATE_COLORS } from "components/hoc/Theme";
import { useCheckAuthMutation, useGetUserQuery } from "features/auth/auth";
import { useEffect, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import ClientViewLayout from "components/clientView/ClientViewLayout";
import LoginPage from "./containers/LoginPage";
import ResetPasswordPage from "./containers/ResetPasswordPage";
import ResetPasswordWithTokenPage from "./containers/ResetPasswordWithTokenPage";
import VerifyEmailPage from "./containers/VerifyEmailPage";
import AccountSettingsPage from "./containers/AccountSettingsPage";
import ClientViewAccountSettingsPage from "./containers/ClientViewAccountSettingsPage";
import RequireAccess from "components/common/RequireAccess";
import IntegrationsExperienceGate from "components/common/IntegrationsExperienceGate";
import ProtectedRoute from "components/common/ProtectedRoute";
import PublicRoute from "components/common/PublicRoute";
import ResetPasswordRoute from "components/common/ResetPasswordRoute";
import ChatPage from "containers/ChatPage";
import HomePage from "containers/HomePage";
import PlatformHomePage from "containers/PlatformHomePage";
import IntegrationsPage from "containers/IntegrationsPage";
import IntegrationDetailsPage from "containers/IntegrationDetailsPage";
import IntegrationsBetaPage from "containers/IntegrationsBetaPage";
import IntegrationBetaDetailsPage from "containers/IntegrationBetaDetailsPage";
import RegistrationWizardPage from "containers/RegistrationWizardPage";
import UserManagementPage from "containers/UserManagementPage";
import SftpManagementPage from "containers/SftpManagementPage";
import BotsManagementPage from "containers/BotsManagementPage";
import PartnerDirectoryPage from "containers/PartnerDirectoryPage";
import PartnerDetailPage from "containers/PartnerDetailPage";
import PartnerManagementPage from "containers/PartnerManagementPage";
import SecureExchangeClientListPage from "containers/SecureExchangeClientListPage";
import SecureExchangeClientPage from "containers/SecureExchangeClientPage";
import SecureExchangeCollectionPage from "containers/SecureExchangeCollectionPage";
import SecureExchangeProjectPage from "containers/SecureExchangeProjectPage";
import { useAppSelector, useAppDispatch } from "features/store";
import { useViewAs } from "hooks/useViewAs";
import { ACTIONS } from "utils/access";
import { logout } from "features/auth/authSlice";
import { localStorageUtils } from "utils/localStorage";
import { useSessionKeepAlive } from "hooks/useSessionKeepAlive";
import { useProfileRefreshOnFocus } from "hooks/useProfileRefreshOnFocus";
import BotsManagementWsConnector from "components/common/BotsManagementWsConnector";
import { FilePreviewProvider } from "components/preview";
import GlobalNotificationStack from "components/notifications/GlobalNotificationStack";
import TopFolderZipJobPoller from "components/topFolderZip/TopFolderZipJobPoller";
import BrowserZipJobPoller from "components/browserZip/BrowserZipJobPoller";
import FolderConnectSyncPoller from "components/folderSync/FolderConnectSyncPoller";
import PdfSplitJobPoller from "components/pdfTools/PdfSplitJobPoller";
import SplitJobLegacyRedirect from "components/botsManagement/split/SplitJobLegacyRedirect";

function BotsMajorJobsLegacyRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/bots-management/${id ?? ""}`} replace />;
}

// The register wizard has no client-view twin (developer-only, top level): a
// shell URL for it lands on the integrations list instead of falling into the
// ":id" details route.
function ClientViewRegisterRedirect() {
  const { href } = useViewAs();
  return <Navigate to={href("/integrations-beta")} replace />;
}

// The per-connection jobs view is retired: nothing links to it any more (the
// task page's Back returns to the major job workspace), so a saved URL lands
// on the user's major jobs list instead of a dead end.
function BotsConnectionLegacyRedirect() {
  const { id } = useParams<{ id: string }>();
  const { basePath } = useViewAs();
  return <Navigate to={`${basePath}/bots-management/${id ?? ""}`} replace />;
}

function App() {
  const [checkAuth] = useCheckAuthMutation();
  const [authInProgress, setAuthInProgress] = useState(true);

  const dispatch = useAppDispatch();
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  // Background jobs belong to the admin's own session.
  const clientViewActive = useAppSelector((state) => state.clientView.active);

  useSessionKeepAlive();
  useProfileRefreshOnFocus(Boolean(isAuthenticated) && !authInProgress);
  const { isLoading: userLoading } = useGetUserQuery(undefined, {
    skip: !isAuthenticated || authInProgress,
  });

  useEffect(() => {
    // Landing on /login with no session: clear the leftovers and show the form.
    // With a live session, leave it alone - PublicRoute sends the user home.
    // Wiping unconditionally logged people out for opening a bookmark, and in
    // the other tabs too, since the tokens are shared (CAL-5974).
    if (window.location.pathname === "/login") {
      if (!localStorage.getItem("refreshToken")) {
        localStorageUtils.clearAppData();
        dispatch(logout());
      }
      setAuthInProgress(false);
      return;
    }

    async function performAuthCheck() {
      try {
        await checkAuth().unwrap();
      } catch {
      } finally {
        setAuthInProgress(false);
      }
    }
    performAuthCheck();
  }, [checkAuth, dispatch]);

  if (authInProgress || userLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        sx={{ bgcolor: CALIBRATE_COLORS.chromeBackground }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalNotificationStack />
      {!clientViewActive && <TopFolderZipJobPoller />}
      {!clientViewActive && <BrowserZipJobPoller />}
      {!clientViewActive && <FolderConnectSyncPoller />}
      {!clientViewActive && <PdfSplitJobPoller />}
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
        <FilePreviewProvider>
          <BrowserRouter>
            <BotsManagementWsConnector />
            <Routes>
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <LoginPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/reset-password"
                element={
                  <ResetPasswordRoute>
                    <ResetPasswordPage />
                  </ResetPasswordRoute>
                }
              />
              <Route
                path="/reset-password/:token"
                element={
                  <PublicRoute>
                    <ResetPasswordWithTokenPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/verify-email/:token"
                element={<VerifyEmailPage />}
              />
              <Route
                path="/integrations"
                element={
                  <RequireAccess surface="integrations">
                    <IntegrationsExperienceGate experience="legacy">
                      <IntegrationsPage />
                    </IntegrationsExperienceGate>
                  </RequireAccess>
                }
              />
              <Route
                path="/integrations/:id"
                element={
                  <RequireAccess surface="integrations">
                    <IntegrationsExperienceGate experience="legacy">
                      <IntegrationDetailsPage />
                    </IntegrationsExperienceGate>
                  </RequireAccess>
                }
              />
              <Route
                path="/integrations-beta"
                element={
                  <ProtectedRoute>
                    <IntegrationsExperienceGate experience="platform">
                      <IntegrationsBetaPage />
                    </IntegrationsExperienceGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/integrations-beta/register"
                element={
                  <RequireAccess
                    action={ACTIONS.INTEGRATIONS_PLATFORM_STRUCTURE}
                  >
                    <RegistrationWizardPage />
                  </RequireAccess>
                }
              />
              <Route
                path="/integrations-beta/:id"
                element={
                  <ProtectedRoute>
                    <IntegrationsExperienceGate experience="platform">
                      <IntegrationBetaDetailsPage />
                    </IntegrationsExperienceGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/user-management"
                element={
                  <RequireAccess surface="user_management">
                    <UserManagementPage />
                  </RequireAccess>
                }
              />
              <Route
                path="/sftp-management"
                element={
                  <RequireAccess surface="sftp_management">
                    <SftpManagementPage />
                  </RequireAccess>
                }
              />
              <Route
                path="/sftp-management/:id"
                element={
                  <RequireAccess surface="sftp_management">
                    <SftpManagementPage />
                  </RequireAccess>
                }
              />
              <Route
                path="/bots-management"
                element={
                  <RequireAccess surface="bots_management">
                    <BotsManagementPage />
                  </RequireAccess>
                }
              />
              <Route
                path="/bots-management/:id"
                element={
                  <RequireAccess surface="bots_management">
                    <BotsManagementPage />
                  </RequireAccess>
                }
              />
              <Route
                path="/bots-management/:id/major-jobs"
                element={
                  <RequireAccess surface="bots_management">
                    <BotsMajorJobsLegacyRedirect />
                  </RequireAccess>
                }
              />
              <Route
                path="/bots-management/:id/major-job/:majorJobId"
                element={
                  <RequireAccess surface="bots_management">
                    <BotsManagementPage />
                  </RequireAccess>
                }
              />
              <Route
                path="/bots-management/:id/major-job/:majorJobId/split-job/:splitJobId"
                element={
                  <RequireAccess surface="bots_management">
                    <BotsManagementPage />
                  </RequireAccess>
                }
              />
              <Route
                path="/bots-management/:id/connections"
                element={
                  <RequireAccess surface="bots_management">
                    <BotsManagementPage />
                  </RequireAccess>
                }
              />
              <Route
                path="/bots-management/:id/bot-notes"
                element={
                  <RequireAccess surface="bots_management">
                    <BotsManagementPage />
                  </RequireAccess>
                }
              />
              <Route
                path="/bots-management/:id/:connectionId/task/:taskId"
                element={
                  <RequireAccess surface="bots_management">
                    <BotsManagementPage />
                  </RequireAccess>
                }
              />
              <Route
                path="/bots-management/:id/:connectionId"
                element={
                  <RequireAccess surface="bots_management">
                    <BotsConnectionLegacyRedirect />
                  </RequireAccess>
                }
              />
              {/* Splitting Tools moved into Bots Management (split major
                  jobs); old links - Slack cards, saved toasts - land there. */}
              <Route
                path="/splitting-tools"
                element={
                  <RequireAccess surface="bots_management">
                    <Navigate to="/bots-management" replace />
                  </RequireAccess>
                }
              />
              <Route
                path="/splitting-tools/:jobId"
                element={
                  <RequireAccess surface="bots_management">
                    <SplitJobLegacyRedirect />
                  </RequireAccess>
                }
              />
              <Route
                path="/client-view/:userId"
                element={
                  <RequireAccess
                    action={ACTIONS.CLIENT_VIEW_OPEN}
                    bySignedInUser
                  >
                    <ClientViewLayout />
                  </RequireAccess>
                }
              >
                <Route index element={<PlatformHomePage />} />
                <Route
                  path="account-settings"
                  element={<ClientViewAccountSettingsPage />}
                />
                <Route
                  path="user-management"
                  element={
                    <RequireAccess surface="user_management">
                      <UserManagementPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="chat"
                  element={
                    <RequireAccess action={ACTIONS.AI_AGENT_OPEN}>
                      <ChatPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="integrations"
                  element={
                    <RequireAccess surface="integrations">
                      <IntegrationsExperienceGate experience="legacy">
                        <IntegrationsPage />
                      </IntegrationsExperienceGate>
                    </RequireAccess>
                  }
                />
                <Route
                  path="integrations/:id"
                  element={
                    <RequireAccess surface="integrations">
                      <IntegrationsExperienceGate experience="legacy">
                        <IntegrationDetailsPage />
                      </IntegrationsExperienceGate>
                    </RequireAccess>
                  }
                />
                <Route
                  path="data-storage"
                  element={
                    <RequireAccess surface="data_storage">
                      <HomePage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="partners"
                  element={
                    <RequireAccess surface="partner_directory">
                      <PartnerDirectoryPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="partners/:slug"
                  element={
                    <RequireAccess surface="partner_directory">
                      <PartnerDetailPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="secure-exchange"
                  element={
                    <RequireAccess surface="secure_exchange">
                      <SecureExchangeClientListPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="secure-exchange/clients/:clientId"
                  element={
                    <RequireAccess surface="secure_exchange">
                      <SecureExchangeClientPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="secure-exchange/clients/:clientId/projects/:projectId"
                  element={
                    <RequireAccess surface="secure_exchange">
                      <SecureExchangeProjectPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="secure-exchange/clients/:clientId/projects/:projectId/collections/:collectionId"
                  element={
                    <RequireAccess surface="secure_exchange">
                      <SecureExchangeCollectionPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="secure-file-upload"
                  element={<Navigate to="../secure-exchange" replace />}
                />
                <Route
                  path="sftp-management"
                  element={
                    <RequireAccess surface="sftp_management">
                      <SftpManagementPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="sftp-management/:id"
                  element={
                    <RequireAccess surface="sftp_management">
                      <SftpManagementPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="partner-management"
                  element={
                    <RequireAccess surface="partner_management">
                      <PartnerManagementPage />
                    </RequireAccess>
                  }
                />
                <Route
                  element={
                    <IntegrationsExperienceGate experience="platform">
                      <Outlet />
                    </IntegrationsExperienceGate>
                  }
                >
                  <Route
                    path="integrations-beta"
                    element={<IntegrationsBetaPage />}
                  />
                  <Route
                    path="integrations-beta/register"
                    element={<ClientViewRegisterRedirect />}
                  />
                  <Route
                    path="integrations-beta/:id"
                    element={<IntegrationBetaDetailsPage />}
                  />
                </Route>
                <Route
                  path="bots-management"
                  element={
                    <RequireAccess surface="bots_management">
                      <BotsManagementPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="bots-management/:id"
                  element={
                    <RequireAccess surface="bots_management">
                      <BotsManagementPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="bots-management/:id/connections"
                  element={
                    <RequireAccess surface="bots_management">
                      <BotsManagementPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="bots-management/:id/bot-notes"
                  element={<BotsManagementPage />}
                />
                <Route
                  path="bots-management/:id/major-job/:majorJobId"
                  element={
                    <RequireAccess surface="bots_management">
                      <BotsManagementPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="bots-management/:id/major-job/:majorJobId/split-job/:splitJobId"
                  element={
                    <RequireAccess surface="bots_management">
                      <BotsManagementPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="bots-management/:id/:connectionId"
                  element={
                    <RequireAccess surface="bots_management">
                      <BotsConnectionLegacyRedirect />
                    </RequireAccess>
                  }
                />
                <Route
                  path="bots-management/:id/:connectionId/task/:taskId"
                  element={
                    <RequireAccess surface="bots_management">
                      <BotsManagementPage />
                    </RequireAccess>
                  }
                />
              </Route>
              <Route
                path="/chat"
                element={
                  <ProtectedRoute>
                    <RequireAccess action={ACTIONS.AI_AGENT_OPEN}>
                      <ChatPage />
                    </RequireAccess>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/account-settings"
                element={
                  <ProtectedRoute>
                    <AccountSettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/data-storage"
                element={
                  <ProtectedRoute>
                    <RequireAccess surface="data_storage">
                      <HomePage />
                    </RequireAccess>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/partners"
                element={
                  <ProtectedRoute>
                    <RequireAccess surface="partner_directory">
                      <PartnerDirectoryPage />
                    </RequireAccess>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/partners/:slug"
                element={
                  <ProtectedRoute>
                    <RequireAccess surface="partner_directory">
                      <PartnerDetailPage />
                    </RequireAccess>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/partner-management"
                element={
                  <RequireAccess surface="partner_management">
                    <PartnerManagementPage />
                  </RequireAccess>
                }
              />
              <Route
                path="/secure-exchange"
                element={
                  <ProtectedRoute>
                    <RequireAccess surface="secure_exchange">
                      <SecureExchangeClientListPage />
                    </RequireAccess>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/secure-exchange/clients/:clientId"
                element={
                  <ProtectedRoute>
                    <RequireAccess surface="secure_exchange">
                      <SecureExchangeClientPage />
                    </RequireAccess>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/secure-exchange/clients/:clientId/projects/:projectId"
                element={
                  <ProtectedRoute>
                    <RequireAccess surface="secure_exchange">
                      <SecureExchangeProjectPage />
                    </RequireAccess>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/secure-exchange/clients/:clientId/projects/:projectId/collections/:collectionId"
                element={
                  <ProtectedRoute>
                    <RequireAccess surface="secure_exchange">
                      <SecureExchangeCollectionPage />
                    </RequireAccess>
                  </ProtectedRoute>
                }
              />
              {/* Old bookmarks and email links keep working. */}
              <Route
                path="/secure-file-upload"
                element={<Navigate to="/secure-exchange" replace />}
              />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <PlatformHomePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="*"
                element={
                  isAuthenticated && user && !user.is_pass_reset ? (
                    <Navigate to="/" replace />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
            </Routes>
          </BrowserRouter>
        </FilePreviewProvider>
      </Box>
    </ThemeProvider>
  );
}

export default App;
